import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Collapsible,
  Heading,
  Page,
  PageContent,
  Paragraph,
  ResponsiveContext,
  Text,
} from 'grommet';

import MarkdownDoc from '../docs/MarkdownDoc';
import useDoc from '../docs/useDoc';
import { buildToc } from '../docs/toc';
import { docNeighbours, findDoc } from '../docs/manifest';
import { ErrorText, Loading } from '../components/StatusMessage';

function TableOfContents({ entries }) {
  const size = useContext(ResponsiveContext);
  const isSmallScreen = size === 'small' || size === 'xsmall';
  const [open, setOpen] = useState(!isSmallScreen);

  if (entries.length < 3) return null;

  const list = (
    <Box as="nav" gap="xsmall" pad={{ vertical: 'small' }}>
      {entries.map(({ id, text, depth }) => (
        <Anchor
          key={id}
          href={`#${id}`}
          size="small"
          margin={{ left: depth === 3 ? 'medium' : 'none' }}
          label={text}
        />
      ))}
    </Box>
  );

  return (
    <Box
      background="light-1"
      round="xsmall"
      pad={{ horizontal: 'medium', vertical: 'small' }}
      margin={{ bottom: 'medium' }}
      flex={false}
    >
      {isSmallScreen ? (
        <>
          <Button
            plain
            onClick={() => setOpen((prev) => !prev)}
            label={<Text weight="bold" size="small">{open ? 'Hide contents' : 'Contents'}</Text>}
            alignSelf="start"
          />
          <Collapsible open={open}>{list}</Collapsible>
        </>
      ) : (
        <>
          <Text weight="bold" size="small">Contents</Text>
          {list}
        </>
      )}
    </Box>
  );
}

export default function DocPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const doc = findDoc(slug);
  const { markdown, status } = useDoc(slug);

  const toc = useMemo(() => (markdown ? buildToc(markdown) : []), [markdown]);

  // Anchor links are only scrollable once the markdown has rendered, so the
  // jump waits for the document rather than happening on navigation.
  useEffect(() => {
    if (status !== 'ready' || !hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [status, hash, markdown]);

  const { previous, next } = docNeighbours(slug);
  // A doc promoted to the header is a standalone page, so "all docs" would
  // point somewhere it isn't listed.
  const showBackLink = !doc || !doc.nav;

  return (
    <Page kind="narrow" pad="medium">
      <PageContent>
        {showBackLink && (
          <Anchor as={Link} to="/docs" size="small" label="← All docs" alignSelf="start" />
        )}

        {!doc && (
          <Box margin={{ vertical: 'medium' }} gap="small" flex={false}>
            <Heading level={1} size="small" margin="none">Page not found</Heading>
            <Paragraph fill margin="none">
              There's no league doc at <Text weight="bold">/docs/{slug}</Text>.
            </Paragraph>
          </Box>
        )}

        {doc && (
          <Box margin={{ top: 'medium' }} flex={false}>
            {status === 'loading' && <Loading>Loading {doc.title}…</Loading>}
            {status === 'error' && (
              <ErrorText>Couldn't load {doc.title}. Try reloading the page.</ErrorText>
            )}
            {status === 'ready' && (
              <>
                <TableOfContents entries={toc} />
                <MarkdownDoc>{markdown}</MarkdownDoc>

                {(previous || next) && (
                  <Box
                    direction="row"
                    justify="between"
                    gap="small"
                    margin={{ top: 'large' }}
                    pad={{ top: 'medium' }}
                    border={{ side: 'top', color: 'light-4' }}
                    flex={false}
                  >
                    {previous
                      ? <Anchor as={Link} to={`/docs/${previous.slug}`} size="small" label={`← ${previous.title}`} />
                      : <span />}
                    {next
                      ? <Anchor as={Link} to={`/docs/${next.slug}`} size="small" label={`${next.title} →`} />
                      : <span />}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </PageContent>
    </Page>
  );
}
